import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import { usePostOrganization } from 'api/aws/organizations_table/usePostOrganization'
import { useCreateContact } from 'api/aws/useCreateContact'
import useSearchData from 'api/search/useSearchData'
import RoadmapBuilder from 'components/RoadmapBuilder/RoadmapBuilder'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Lead } from 'types'
import { useActiveUser } from '../../api/slack/useActiveUser'

// ─── Fuzzy matching ─────────────────────────────────────────────────
function normalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function fuzzyScore(a: string, b: string): number {
  if (!a || !b) return 0
  const na = normalize(a)
  const nb = normalize(b)
  if (!na || !nb) return 0
  if (na === nb) return 1
  // Only count substring match if the shorter string is meaningful (3+ chars)
  const shorter = na.length <= nb.length ? na : nb
  const longer = na.length <= nb.length ? nb : na
  if (shorter.length >= 3 && longer.includes(shorter)) return 0.8
  // Token overlap — normalize tokens and filter out trivial words
  const stopWords = new Set(['the', 'inc', 'llc', 'ltd', 'co', 'corp', 'and', 'of', 'a'])
  const toTokens = (s: string) => {
    return new Set(
      s
        .toLowerCase()
        .split(/\s+/)
        .map(t => t.replace(/[^a-z0-9]/g, ''))
        .filter(t => t.length > 1 && !stopWords.has(t))
    )
  }
  const tokA = toTokens(a)
  const tokB = toTokens(b)
  if (tokA.size === 0 || tokB.size === 0) return 0
  const intersection = [...tokA].filter(t => tokB.has(t))
  const union = new Set([...tokA, ...tokB])
  return intersection.length / union.size
}

// ─── Constants ──────────────────────────────────────────────────────
const STEPS = ['Organization', 'Contact', 'Roadmap']
const MATCH_THRESHOLD = 0.5

const nonHousingVerticals = [
  'Construction',
  'Tourism',
  'Marketing Agency',
  'Manufacturing',
  'Entertainment',
  'Restaurant (Food & Beverage)',
  'City/Government',
  'Financial Institution',
  'Technology',
  'Medical',
  'Lawyer',
  'Education',
  'Mortgage',
  'Other',
  'Real Estate',
]
const housingVerticals = ['Student', 'Multifamily', 'Homebuilding', 'Senior', 'Build to rent']
const HOUSING_VERTICALS_FOR_DEAL = ['Student', 'Multifamily']
const allVerticals = [...nonHousingVerticals, ...housingVerticals].sort()

const typeOptions = [
  { label: 'Parent Client', value: 'PC' },
  { label: 'Agency Whitelabel', value: 'AW' },
  { label: 'Organization', value: 'ORG' },
  {
    label: 'Property Management Company',
    type: 'PMC',
  },
  { label: 'Ownership Group', type: 'OG' },
  { label: 'Housing Property', type: 'HP' },
]

// ─── Component ──────────────────────────────────────────────────────

interface ConvertLeadDialogProps {
  open: boolean
  onClose: () => void
  lead: Lead
}

const ConvertLeadDialog = ({ open, onClose, lead }: ConvertLeadDialogProps) => {
  const { activeUser } = useActiveUser()
  const { organizations } = useSearchData()
  const postOrg = usePostOrganization()
  const createContact = useCreateContact()

  const [activeStep, setActiveStep] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Resolved org after step 0 completes
  const [resolvedOrgId, setResolvedOrgId] = useState<string | null>(null)
  const [resolvedOrgName, setResolvedOrgName] = useState<string | null>(null)
  const [resolvedOrgIndustry, setResolvedOrgIndustry] = useState<string>('')

  // ── Org state ───────────────────────────────────────────
  const [useExistingOrg, setUseExistingOrg] = useState<boolean | null>(null)
  const [selectedOrg, setSelectedOrg] = useState<any>(null)
  const [newOrg, setNewOrg] = useState({
    name: lead?.company || '',
    type: 'ORG',
    status: 'Prospect',
    vertical: [] as string[],
  })

  // ── Contact state ───────────────────────────────────────
  const [contactData, setContactData] = useState(() => {
    const parts = (lead?.name || '').split(' ')
    return {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || '',
      email: lead?.email || '',
      phone: lead?.phone || '',
      title: '',
    }
  })

  // ── Fuzzy org matches ───────────────────────────────────
  const orgMatches = useMemo(() => {
    if (!lead?.company || !organizations?.length) return []
    return organizations
      .map(org => ({
        ...org,
        score: Math.max(fuzzyScore(lead.company, org.name || ''), fuzzyScore(lead.company, org.dba || '')),
      }))
      .filter(org => org.score >= MATCH_THRESHOLD)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
  }, [lead?.company, organizations])

  // Auto-detect if there's a strong match
  useEffect(() => {
    if (orgMatches.length > 0 && orgMatches[0].score >= 0.8) {
      setUseExistingOrg(true)
      setSelectedOrg(orgMatches[0])
    } else {
      setUseExistingOrg(false)
    }
  }, [orgMatches])

  // ── Validation ──────────────────────────────────────────
  const isOrgStepValid = useExistingOrg ? !!selectedOrg : newOrg.name.trim() !== '' && newOrg.vertical.length > 0

  const isContactStepValid =
    contactData.firstName.trim() !== '' && contactData.lastName.trim() !== '' && contactData.email.trim() !== ''

  const canProceed = activeStep === 0 ? isOrgStepValid : activeStep === 1 ? isContactStepValid : false

  // ── Advance: resolve org + contact before opening roadmap ────────
  const handleNext = useCallback(async () => {
    if (activeStep === 0) {
      // Resolve org
      setIsSubmitting(true)
      try {
        let orgUuid: string
        let orgName: string
        let orgIndustry: string = ''
        if (useExistingOrg && selectedOrg) {
          orgUuid = selectedOrg.org_uuid
          orgName = selectedOrg.name
          orgIndustry = Array.isArray(selectedOrg.vertical) ? selectedOrg.vertical[0] || '' : ''
        } else {
          const result: any = await new Promise((resolve, reject) => {
            postOrg.mutate(
              { name: newOrg.name, type: newOrg.type, status: newOrg.status, vertical: newOrg.vertical, owner: [activeUser?.user?.id || ''] } as any,
              { onSuccess: resolve, onError: reject }
            )
          })
          orgUuid = result?.org_uuid
          orgName = newOrg.name
          orgIndustry = newOrg.vertical[0] || ''
        }
        setResolvedOrgId(orgUuid)
        setResolvedOrgName(orgName)
        setResolvedOrgIndustry(orgIndustry)
        setActiveStep(1)
      } catch (err) {
        console.error('Org step failed:', err)
      } finally {
        setIsSubmitting(false)
      }
    } else if (activeStep === 1) {
      // Create contact then open roadmap
      setIsSubmitting(true)
      try {
        await new Promise((resolve, reject) => {
          createContact.mutate(
            {
              firstName: contactData.firstName,
              lastName: contactData.lastName,
              name: `${contactData.firstName} ${contactData.lastName}`,
              email: contactData.email,
              phone: contactData.phone,
              title: contactData.title,
              organizations: [resolvedOrgId],
            },
            { onSuccess: resolve, onError: reject }
          )
        })
        setActiveStep(2)
      } catch (err) {
        console.error('Contact step failed:', err)
      } finally {
        setIsSubmitting(false)
      }
    }
  }, [activeStep, useExistingOrg, selectedOrg, newOrg, contactData, resolvedOrgId, activeUser, postOrg, createContact])

  // ─── Render Steps ──────────────────────────────────────────────────

  const renderOrgStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {orgMatches.length > 0 && (
        <Alert
          severity={orgMatches[0].score >= 0.8 ? 'warning' : 'info'}
          icon={orgMatches[0].score >= 0.8 ? <WarningAmberIcon /> : undefined}
        >
          {orgMatches[0].score >= 0.8
            ? `"${lead.company}" closely matches existing organization "${orgMatches[0].name}"`
            : `Found ${orgMatches.length} potential match(es) for "${lead.company}"`}
        </Alert>
      )}

      {orgMatches.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={useExistingOrg ? 'contained' : 'outlined'}
            size="small"
            onClick={() => {
              setUseExistingOrg(true)
              if (!selectedOrg && orgMatches.length > 0) setSelectedOrg(orgMatches[0])
            }}
          >
            Use Existing
          </Button>
          <Button
            variant={useExistingOrg === false ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setUseExistingOrg(false)}
          >
            Create New
          </Button>
        </Box>
      )}

      {useExistingOrg ? (
        <Autocomplete
          options={orgMatches}
          value={selectedOrg}
          onChange={(_, val) => setSelectedOrg(val)}
          getOptionLabel={(opt: any) =>
            `${opt.name}${opt.dba ? ` (${opt.dba})` : ''} — ${Math.round(opt.score * 100)}% match`
          }
          renderInput={params => <TextField {...params} label="Select Organization" />}
        />
      ) : (
        <>
          <TextField
            label="Organization Name"
            value={newOrg.name}
            onChange={e => setNewOrg(prev => ({ ...prev, name: e.target.value }))}
            required
            fullWidth
          />
          <Select
            value={newOrg.type}
            onChange={e => setNewOrg(prev => ({ ...prev, type: e.target.value }))}
            displayEmpty
            fullWidth
          >
            {typeOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          <Autocomplete
            multiple
            options={allVerticals}
            value={newOrg.vertical}
            onChange={(_, val) => setNewOrg(prev => ({ ...prev, vertical: val }))}
            renderInput={params => <TextField {...params} label="Industry Vertical(s)" required />}
            renderTags={(value, getTagProps) =>
              value.map((opt, idx) => <Chip {...getTagProps({ index: idx })} key={opt} label={opt} size="small" />)
            }
          />
        </>
      )}
    </Box>
  )

  const renderContactStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Create a contact from the lead data. This contact will be linked to the organization
        {useExistingOrg ? ` "${selectedOrg?.name}"` : ` "${newOrg.name}"`}.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          label="First Name"
          value={contactData.firstName}
          onChange={e => setContactData(prev => ({ ...prev, firstName: e.target.value }))}
          required
          fullWidth
        />
        <TextField
          label="Last Name"
          value={contactData.lastName}
          onChange={e => setContactData(prev => ({ ...prev, lastName: e.target.value }))}
          required
          fullWidth
        />
      </Box>
      <TextField
        label="Email"
        value={contactData.email}
        onChange={e => setContactData(prev => ({ ...prev, email: e.target.value }))}
        required
        fullWidth
      />
      <TextField
        label="Phone"
        value={contactData.phone}
        onChange={e => setContactData(prev => ({ ...prev, phone: e.target.value }))}
        fullWidth
      />
      <TextField
        label="Title"
        value={contactData.title}
        onChange={e => setContactData(prev => ({ ...prev, title: e.target.value }))}
        fullWidth
      />
    </Box>
  )

  const isRoadmapStep = activeStep === 2

  return (
    <Dialog open={open} onClose={onClose} maxWidth={isRoadmapStep ? false : 'sm'} fullWidth fullScreen={isRoadmapStep}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Convert Lead to Deal
        <Chip label={lead?.name || 'Unknown'} size="small" sx={{ ml: 1 }} />
        {isRoadmapStep && resolvedOrgName && (
          <Chip label={resolvedOrgName} size="small" color="primary" sx={{ ml: 0.5 }} />
        )}
      </DialogTitle>

      <DialogContent sx={isRoadmapStep ? { p: 0, display: 'flex', flexDirection: 'column' } : undefined}>
        {!isRoadmapStep && (
          <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
            {STEPS.map((label, idx) => (
              <Step key={label} completed={idx < activeStep}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {activeStep === 0 && renderOrgStep()}
        {activeStep === 1 && renderContactStep()}
        {isRoadmapStep && resolvedOrgId && (
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <RoadmapBuilder
              initialClientOrgId={resolvedOrgId}
              initialClientName={resolvedOrgName ?? ''}
              initialClientIndustry={resolvedOrgIndustry}
              onComplete={onClose}
            />
          </Box>
        )}
      </DialogContent>

      {!isRoadmapStep && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          {activeStep > 0 && (
            <Button onClick={() => setActiveStep(prev => prev - 1)} disabled={isSubmitting}>
              Back
            </Button>
          )}
          <Button variant="contained" onClick={handleNext} disabled={!canProceed || isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Next'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default ConvertLeadDialog
